<ion-grid>
  <!-- Header with Stats -->
  <ion-row>
    <ion-col>
      <ion-card>
        <ion-card-header>
          <ion-card-title>Knowledge Gap Insights</ion-card-title>
          <ion-card-subtitle
            >AI-detected insights and actionable recommendations for your
            knowledge base</ion-card-subtitle
          >
        </ion-card-header>
      </ion-card>
    </ion-col>
  </ion-row>

  <!-- Filters and Search -->
  <ion-row>
    <ion-col>
      <div class="filter-controls">
        <ion-searchbar
          mode="ios"
          placeholder="Search insights..."
          showCancelButton="never"
          class="rounded-search-bar"
          [(ngModel)]="searchText"
          (ionChange)="onSearch()"
        >
        </ion-searchbar>

        <app-filter-button
          [options]="filterOptions"
          (didChange)="filtersDidChange($event)"
        >
        </app-filter-button>
      </div>
    </ion-col>
  </ion-row>

  <!-- Insights List -->
  <ion-row>
    <ion-col>
      <!-- Loading State -->
      <div *ngIf="isLoading" class="loading-container">
        <ion-spinner></ion-spinner>
        <p>Loading insights...</p>
      </div>

      <!-- Insights List -->
      <div
        *ngIf="!isLoading && insightsDataSource.lastResponse?.data?.length > 0"
      >
        <ion-card
          *ngFor="
            let insight of insightsDataSource.lastResponse.data;
            trackBy: trackByInsight
          "
          class="insight-card"
        >
          <ion-card-content>
            <!-- Insight Header -->
            <div class="category-header">
              <h2>{{ insight.category }}</h2>
            </div>

            <div class="metadata">
              <span class="conversation-count">
                {{ getTotalConversationCount(insight) }} conversations
              </span>
              <span class="date">
                {{ displayDate(insight.createdAt) }}
              </span>
            </div>

            <!-- Recommendations Section -->
            <div
              *ngIf="getNonDismissedActions(insight).length > 0"
              style="
                margin-top: 1rem;
                padding-top: 1rem;
                border-top: 1px solid var(--ion-color-light);
              "
            >
              <h3 style="margin: 0 0 0.75rem 0; font-size: 0.95rem">
                Recommendations
              </h3>

              <div
                *ngFor="
                  let action of getNonDismissedActions(insight);
                  let i = index
                "
                style="margin-bottom: 1rem"
              >
                <!-- Action Row -->
                <div
                  style="
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 0.5rem;
                  "
                >
                  <ion-badge
                    *ngIf="action.type === 'create_new'"
                    color="success"
                  >
                    Create
                  </ion-badge>
                  <ion-badge
                    *ngIf="action.type === 'update_existing'"
                    color="tertiary"
                  >
                    Update
                  </ion-badge>
                  <!-- Main Action Button -->
                  <ion-button
                    *ngIf="!action.isDismissed"
                    fill="clear"
                    color="dark"
                    class="button-dark-border"
                    (click)="executeAction(insight, action)"
                  >
                    {{ action.description }}
                    <ion-icon name="open-outline" slot="end"></ion-icon>
                  </ion-button>

                  <!-- Restore Button (for dismissed actions) -->
                  <ion-button
                    *ngIf="action.isDismissed"
                    fill="outline"
                    color="secondary"
                    size="default"
                    (click)="undismissAction(insight, action)"
                  >
                    {{ action.description }}
                    <ion-icon name="refresh" slot="end"></ion-icon>
                  </ion-button>

                  <!-- Dismiss X Button -->
                  <ion-button
                    *ngIf="!action.isDismissed"
                    fill="clear"
                    class="btn-small"
                    color="medium"
                    (click)="dismissAction(insight, action)"
                  >
                    <ion-icon name="close" slot="icon-only"></ion-icon>
                  </ion-button>
                </div>

                <!-- Show Related Conversations Toggle -->
                <ion-button
                  fill="clear"
                  size="small"
                  (click)="toggleActionConversations(insight.id, i)"
                >
                  <ion-icon
                    [name]="
                      isActionExpanded(insight.id, i)
                        ? 'chevron-up'
                        : 'chevron-down'
                    "
                    slot="start"
                  ></ion-icon>
                  {{
                    getExpandButtonText(
                      insight,
                      action,
                      isActionExpanded(insight.id, i)
                    )
                  }}
                </ion-button>

                <!-- Expandable Conversations List -->
                <div
                  *ngIf="isActionExpanded(insight.id, i)"
                  style="
                    margin-top: 0.5rem;
                    padding-top: 0.5rem;
                    border-top: 1px solid var(--ion-color-light);
                  "
                >
                  <ion-item
                    *ngFor="let topic of getTopicsForAction(insight, action)"
                    lines="none"
                    style="--padding-start: 0; --inner-padding-end: 0"
                  >
                    <ion-label>
                      <h4 style="margin: 0; font-size: 0.85rem">
                        <a
                          [href]="getConversationUrlByTopic(topic)"
                          target="_blank"
                          style="
                            color: var(--ion-color-primary);
                            text-decoration: none;
                          "
                        >
                          {{ getConversationTitle(topic) }}
                        </a>
                      </h4>
                      <p
                        style="
                          margin: 0.25rem 0 0 0;
                          font-size: 0.75rem;
                          color: var(--ion-color-medium);
                        "
                      >
                        {{ topic.conversation.summary }}
                      </p>
                    </ion-label>
                  </ion-item>
                </div>
              </div>
            </div>

            <!-- No Actions State -->
            <div
              *ngIf="getNonDismissedActions(insight).length === 0"
              style="
                margin-top: 1rem;
                padding-top: 1rem;
                border-top: 1px solid var(--ion-color-light);
                text-align: center;
                color: var(--ion-color-medium);
              "
            >
              <p style="margin: 0; font-size: 0.85rem">
                No actions available for this insight
              </p>
            </div>
          </ion-card-content>
        </ion-card>
      </div>

      <!-- Empty State -->
      <div
        *ngIf="
          !isLoading &&
          (!insightsDataSource.lastResponse ||
            insightsDataSource.lastResponse.data.length === 0)
        "
        class="empty-state"
      >
        <ion-card>
          <ion-card-content class="ion-text-center">
            <ion-icon
              name="bulb-outline"
              size="large"
              color="medium"
            ></ion-icon>
            <h3>No Knowledge Gap Insights</h3>
            <p>No insights found. This could mean:</p>
            <ul>
              <li>Your knowledge base is comprehensive</li>
              <li>No patterns have been detected yet</li>
              <li>Try adjusting your filters</li>
            </ul>
          </ion-card-content>
        </ion-card>
      </div>

      <!-- Pagination Controls -->
      <div
        *ngIf="!isLoading && insightsDataSource.lastResponse?.data?.length > 0"
        class="pagination-container"
      >
        <ion-button
          fill="clear"
          [disabled]="
            insightsDataSource.lastResponse?.cursors?.hasPrevious !== true
          "
          (click)="previousPage()"
        >
          <ion-icon name="chevron-back" slot="start"></ion-icon>
          Previous
        </ion-button>
        <span class="pagination-info">
          {{ insightsDataSource.lastResponse?.data?.length || 0 }}
          insights
        </span>
        <ion-button
          fill="clear"
          [disabled]="
            insightsDataSource.lastResponse?.cursors?.hasNext !== true
          "
          (click)="nextPage()"
        >
          Next
          <ion-icon name="chevron-forward" slot="end"></ion-icon>
        </ion-button>
      </div>
    </ion-col>
  </ion-row>
</ion-grid>
